<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import ContentDropdown from './ContentDropdown.vue';
import BaseCheckbox from './BaseCheckbox.vue';
import BaseButton from './BaseButton.vue';
import BaseTextInput from './BaseTextInput.vue';
import RoomSelector from './RoomSelector.vue';
import { useModalStore, ModalType } from '@/stores/modal.ts';
import type { DateRange, RoomOccupancy } from '@/types/SearchBar.ts';
import type { Country } from '@/types/schemas';
import type { DisabledParameterField } from '@/types/SearchDefaults';
import { $t } from '@/composables/translations/useTranslate.ts';
import { formatOccupancySummary } from '@/utils/formatting/formatOccupancySummary.ts';

interface Props {
  countries?: Country[];
  selectedCountryIds?: string[];
  dateRange?: DateRange;
  roomOccupancy?: RoomOccupancy;
  disabled?: boolean;
  disabledFields?: readonly DisabledParameterField[];
  loading?: boolean;
  wrapperClass?: string;
  containerClass?: string;
  hideDestination?: boolean;
  autoDetectInitialValues?: boolean;
}

type ButtonType = 'destination' | 'date' | 'companions';

const props = withDefaults(defineProps<Props>(), {
  countries: () => [],
  selectedCountryIds: () => [],
  dateRange: null,
  roomOccupancy: () => ({ rooms: [] }),
  disabled: false,
  disabledFields: () => [],
  loading: false,
  wrapperClass: '',
  containerClass: '',
  hideDestination: false,
  autoDetectInitialValues: false,
});

const isDestDisabled = computed(() => props.disabledFields.includes('dest'));
const isDateDisabled = computed(() => props.disabledFields.includes('date'));
const isPaxDisabled = computed(() => props.disabledFields.includes('pax'));

const emit = defineEmits<{
  'countries-change': [selectedIds: string[]];
  'room-occupancy-change': [roomOccupancy: RoomOccupancy];
  'search-click': [];
}>();

const searchBarContainer = ref<HTMLElement | null>(null);
const destinationDropdownRef = ref<InstanceType<typeof ContentDropdown> | null>(null);
const companionsDropdownRef = ref<InstanceType<typeof ContentDropdown> | null>(null);
const roomSelectorRef = ref<InstanceType<typeof RoomSelector> | null>(null);
const modalStore = useModalStore();

const dropdownStates = ref<Record<'destination' | 'companions', boolean>>({
  destination: false,
  companions: false,
});

const hoverState = ref<Record<ButtonType, boolean>>({
  destination: false,
  date: false,
  companions: false,
});

const localSelectedCountryIds = ref<string[]>([]);
const localRoomOccupancy = ref<RoomOccupancy>({ rooms: [] });
const hasUserInteracted = ref(false);
const hasDateInteracted = ref(false);
const searchQuery = ref('');
const isPulsing = ref(false);

const lastHoveredIndex = ref(-1);
const lastActiveIndex = ref(-1);

const filteredCountries = computed(() => {
  if (!searchQuery.value) {
    return props.countries;
  }
  const query = searchQuery.value.toLowerCase();
  return props.countries.filter((country) => country.Name.toLowerCase().includes(query));
});

const getButtonClasses = (isActive: boolean, isDisabled = false) => {
  const baseClasses =
    'flex items-center justify-center gap-2.5 px-5 lg:px-10 py-4 transition-colors';
  const focusClasses = 'focus:outline-2 focus:outline-text-primary focus:outline-offset-2';
  const activeClasses = isActive ? 'text-white' : isDisabled ? '' : 'hover:text-white';
  return `${baseClasses} ${focusClasses} ${activeClasses}`;
};

const destinationButtonClasses = computed(() =>
  getButtonClasses(dropdownStates.value.destination, isDestDisabled.value)
);
const dateButtonClasses = computed(() =>
  getButtonClasses(modalStore.isModalOpen(ModalType.DATE_PICKER), isDateDisabled.value)
);
const companionsButtonClasses = computed(() =>
  getButtonClasses(dropdownStates.value.companions, isPaxDisabled.value)
);

const activeButtonIndex = computed(() => {
  if (props.hideDestination) {
    if (modalStore.isModalOpen(ModalType.DATE_PICKER)) return 0;
    if (dropdownStates.value.companions) return 1;
    return -1;
  }
  if (dropdownStates.value.destination) return 0;
  if (modalStore.isModalOpen(ModalType.DATE_PICKER)) return 1;
  if (dropdownStates.value.companions) return 2;
  return -1;
});

const hoveredButtonIndex = computed(() => {
  const destHover = hoverState.value.destination && !isDestDisabled.value;
  const dateHover = hoverState.value.date && !isDateDisabled.value;
  const paxHover = hoverState.value.companions && !isPaxDisabled.value;

  if (props.hideDestination) {
    if (dateHover) return 0;
    if (paxHover) return 1;
    return -1;
  }
  if (destHover) return 0;
  if (dateHover) return 1;
  if (paxHover) return 2;
  return -1;
});

const getBackgroundStyle = (index: number, opacity: string) => ({
  opacity: index === -1 ? '0' : opacity,
  transform: `translateX(${Math.max(0, index) * 100}%)`,
});

const activeBackgroundStyle = computed(() => {
  const currentIndex = activeButtonIndex.value;
  const displayIndex = currentIndex === -1 ? lastActiveIndex.value : currentIndex;
  return getBackgroundStyle(displayIndex, currentIndex === -1 ? '0' : '1');
});

const hoverBackgroundStyle = computed(() => {
  const index = hoveredButtonIndex.value;
  const activeIndex = activeButtonIndex.value;

  if (index === activeIndex && index !== -1) {
    return getBackgroundStyle(lastHoveredIndex.value, '0');
  }

  if (index !== -1) {
    return getBackgroundStyle(index, '0.7');
  }

  return getBackgroundStyle(lastHoveredIndex.value, '0');
});

const searchButtonClasses = computed(() => {
  const baseClasses = 'flex items-center p-3 rounded-full bg-actions-primary transition-colors';
  const focusClasses = 'focus:outline-2 focus:outline-text-primary focus:outline-offset-2';
  const disabledClasses = props.disabled
    ? 'opacity-50 cursor-not-allowed'
    : 'hover:bg-actions-primary-hover active:bg-actions-primary-active';

  return `${baseClasses} ${focusClasses} ${disabledClasses}`;
});

const destinationDisplayLabel = computed(() => {
  if (!props.selectedCountryIds || props.selectedCountryIds.length === 0) {
    return $t('ibe.search.where-to', 'Where to travel?');
  }

  const countryNames = props.selectedCountryIds.map((code) => {
    const country = props.countries.find((c) => c.Code === code);
    return country?.Name || code;
  });

  return countryNames.join(', ');
});

const dateDisplayLabel = computed(() => {
  if (!hasDateInteracted.value && !isDateDisabled.value) {
    return $t('ibe.search.when-departure', 'When do we leave?');
  }

  if (!props.dateRange || !props.dateRange[0] || !props.dateRange[1]) {
    return $t('ibe.search.when-departure', 'When do we leave?');
  }

  const formatDate = (date: Date, includeYear: boolean = false) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return includeYear ? `${day}.${month}.${year}` : `${day}.${month}.`;
  };

  const startYear = props.dateRange[0].getFullYear();
  const endYear = props.dateRange[1].getFullYear();
  const differentYears = startYear !== endYear;

  const startDate = formatDate(props.dateRange[0], differentYears);
  const endDate = formatDate(props.dateRange[1], true);

  return `${startDate} - ${endDate}`;
});

const companionsDisplayLabel = computed(() => {
  if (!hasUserInteracted.value && !isPaxDisabled.value) {
    return $t('ibe.search.who-with-whom', 'Who with whom?');
  }

  const rooms = localRoomOccupancy.value.rooms;
  if (!rooms || rooms.length === 0) {
    return $t('ibe.search.who-with-whom', 'Who with whom?');
  }

  return (
    formatOccupancySummary(localRoomOccupancy.value) ||
    $t('ibe.search.who-with-whom', 'Who with whom?')
  );
});

const destinationAriaLabel = computed(
  () =>
    `${$t('ibe.search.change-destination', 'Change destination')}: ${destinationDisplayLabel.value}`
);
const dateAriaLabel = computed(
  () => `${$t('ibe.search.change-dates', 'Change travel dates')}: ${dateDisplayLabel.value}`
);
const companionsAriaLabel = computed(
  () => `${$t('ibe.search.change-travelers', 'Change travellers')}: ${companionsDisplayLabel.value}`
);

// Initialize local selected country IDs from props
watch(
  () => props.selectedCountryIds,
  (newIds) => {
    localSelectedCountryIds.value = newIds ? [...newIds] : [];
  },
  { immediate: true }
);

// Initialize local room occupancy from props
watch(
  () => props.roomOccupancy,
  (newOccupancy) => {
    localRoomOccupancy.value = newOccupancy ? { ...newOccupancy } : { rooms: [] };

    // Auto-detect initial values if enabled
    if (props.autoDetectInitialValues && newOccupancy && newOccupancy.rooms.length > 0) {
      hasUserInteracted.value = true;
    }
  },
  { immediate: true }
);

// Auto-detect initial date range if enabled
watch(
  () => props.dateRange,
  (newDateRange) => {
    if (props.autoDetectInitialValues && newDateRange && newDateRange[0] && newDateRange[1]) {
      hasDateInteracted.value = true;
    }
  },
  { immediate: true }
);

const closeDropdown = (type: 'destination' | 'companions', shouldEmitChanges: boolean = false) => {
  dropdownStates.value[type] = false;

  if (type === 'destination') {
    if (shouldEmitChanges) {
      emit('countries-change', [...localSelectedCountryIds.value]);
    } else {
      localSelectedCountryIds.value = props.selectedCountryIds ? [...props.selectedCountryIds] : [];
    }
  }
};

const handleCountryToggle = (countryCode: string, checked: boolean) => {
  if (checked) {
    if (!localSelectedCountryIds.value.includes(countryCode)) {
      localSelectedCountryIds.value.push(countryCode);
    }
  } else {
    const index = localSelectedCountryIds.value.indexOf(countryCode);
    if (index > -1) {
      localSelectedCountryIds.value.splice(index, 1);
    }
  }
};

const handleApplyCountries = (close?: () => void) => {
  closeDropdown('destination', true);
  if (close) {
    close();
  } else {
    destinationDropdownRef.value?.closeDropdown();
  }
};

const handleDestinationDropdownClose = () => {
  closeDropdown('destination', true);
};

const handleDestinationDropdownOpen = () => {
  if (isDestDisabled.value) {
    destinationDropdownRef.value?.closeDropdown();
    return;
  }
  dropdownStates.value.destination = true;

  dropdownStates.value.companions = false;
  companionsDropdownRef.value?.closeDropdown();
  modalStore.closeModal(ModalType.DATE_PICKER);

  localSelectedCountryIds.value = props.selectedCountryIds ? [...props.selectedCountryIds] : [];
  searchQuery.value = '';
};

const handleDateClick = () => {
  if (isDateDisabled.value) return;
  isPulsing.value = false;
  hasDateInteracted.value = true;

  dropdownStates.value.destination = false;
  dropdownStates.value.companions = false;
  destinationDropdownRef.value?.closeDropdown();
  companionsDropdownRef.value?.closeDropdown();

  modalStore.openModal(ModalType.DATE_PICKER);
};

const handleCompanionsDropdownClose = () => {
  roomSelectorRef.value?.handleAutoSave();
  closeDropdown('companions', false);
};

const handleCompanionsDropdownOpen = () => {
  if (isPaxDisabled.value) {
    companionsDropdownRef.value?.closeDropdown();
    return;
  }
  isPulsing.value = false;
  dropdownStates.value.companions = true;
  dropdownStates.value.destination = false;
  destinationDropdownRef.value?.closeDropdown();
  modalStore.closeModal(ModalType.DATE_PICKER);

  localRoomOccupancy.value = props.roomOccupancy ? { ...props.roomOccupancy } : { rooms: [] };
};

const handleRoomOccupancyConfirm = (close?: () => void) => {
  hasUserInteracted.value = true;

  emit('room-occupancy-change', { ...localRoomOccupancy.value });
  closeDropdown('companions', true);

  if (close) {
    close();
  } else {
    companionsDropdownRef.value?.closeDropdown();
  }
};

const handleRoomOccupancyAutoSave = (roomOccupancy: RoomOccupancy) => {
  hasUserInteracted.value = true;
  localRoomOccupancy.value = { ...roomOccupancy };
  emit('room-occupancy-change', { ...roomOccupancy });
};

const handleSearchClick = () => {
  if (!props.disabled && !props.loading) {
    emit('search-click');
  }
};

const closeAllDropdowns = (shouldEmitChanges: boolean = false) => {
  Object.keys(dropdownStates.value).forEach((key) => {
    const dropdownKey = key as 'destination' | 'companions';
    if (dropdownStates.value[dropdownKey]) {
      closeDropdown(dropdownKey, shouldEmitChanges);
    }
  });

  destinationDropdownRef.value?.closeDropdown();
  companionsDropdownRef.value?.closeDropdown();
};

const handleOutsideClick = (event: MouseEvent) => {
  if (searchBarContainer.value && !searchBarContainer.value.contains(event.target as Node)) {
    closeAllDropdowns(false);
  }
};

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick);
});

watch(hoveredButtonIndex, (newIndex) => {
  if (newIndex !== -1) {
    lastHoveredIndex.value = newIndex;
  }
});

watch(activeButtonIndex, (newIndex) => {
  if (newIndex !== -1) {
    lastActiveIndex.value = newIndex;
  }
});

// Expose method for programmatic control
function openCompanionsDropdown() {
  dropdownStates.value.destination = false;
  destinationDropdownRef.value?.closeDropdown();
  modalStore.closeModal(ModalType.DATE_PICKER);

  companionsDropdownRef.value?.openDropdown();
  dropdownStates.value.companions = true;

  localRoomOccupancy.value = props.roomOccupancy ? { ...props.roomOccupancy } : { rooms: [] };
}

// Draw the user's attention to the date + companions buttons. Pulses until
// the user interacts with either of them (handleDateClick /
// handleCompanionsDropdownOpen stop it).
function pulseAttention() {
  isPulsing.value = true;
}

defineExpose({
  openCompanionsDropdown,
  pulseAttention,
});
</script>

<template>
  <div
    ref="searchBarContainer"
    :class="`flex flex-col md:flex-row items-center w-full justify-center gap-2.5 wrapper ${wrapperClass}`"
  >
    <div
      :class="`relative grid items-center ${hideDestination ? 'grid-cols-2' : 'grid-cols-3'} bg-white border rounded-full border-actions-primary ${containerClass}`"
    >
      <div
        :class="`absolute inset-y-0 ${hideDestination ? 'w-1/2' : 'w-1/3'} bg-actions-primary rounded-full transition-all duration-300 ease-in-out`"
        :style="hoverBackgroundStyle"
      ></div>
      <div
        :class="`absolute inset-y-0 ${hideDestination ? 'w-1/2' : 'w-1/3'} bg-actions-primary rounded-full transition-all duration-300 ease-in-out shadow-lg`"
        :style="activeBackgroundStyle"
      ></div>

      <div
        v-if="!hideDestination"
        class="absolute w-px h-6 -translate-y-1/2 bg-gray-500 left-1/3 top-1/2"
      ></div>
      <div
        v-if="!hideDestination"
        class="absolute w-px h-6 -translate-y-1/2 bg-gray-500 left-2/3 top-1/2"
      ></div>
      <div
        v-if="hideDestination"
        class="absolute w-px h-6 -translate-y-1/2 bg-gray-500 left-1/2 top-1/2"
      ></div>

      <ContentDropdown
        v-if="!hideDestination"
        ref="destinationDropdownRef"
        placement="bottom-start"
        :close-on-click="false"
        :disabled="isDestDisabled"
        :trigger-aria-label="destinationAriaLabel"
        min-width="min-w-[310px]"
        min-height="min-h-[200px]"
        @open="handleDestinationDropdownOpen"
        @close="handleDestinationDropdownClose"
      >
        <template #trigger>
          <div
            :class="destinationButtonClasses"
            class="relative z-10 max-w-[240px] min-w-0"
            @mouseenter="hoverState.destination = true"
            @mouseleave="hoverState.destination = false"
          >
            <span class="block min-w-0 text-sm font-medium truncate lg:text-base">{{
              destinationDisplayLabel
            }}</span>
          </div>
        </template>

        <template #default="{ close }">
          <div class="space-y-4">
            <div class="relative">
              <BaseTextInput
                v-model="searchQuery"
                name="destination-search"
                type="search"
                :placeholder="$t('ibe.search.search-country', 'Search country')"
                wrapper-class="!mb-0"
                input-class="!min-w-0 w-full pr-10"
              />
              <i
                v-if="!searchQuery"
                class="absolute w-5 h-5 -translate-y-1/2 pointer-events-none right-3 top-1/2 bg-text-primary icon icon-search"
              ></i>
            </div>
            <div class="space-y-3 overflow-y-auto max-h-64">
              <BaseCheckbox
                v-for="country in filteredCountries"
                :id="`country-${country.Code}`"
                :key="country.Code"
                :label="country.Name"
                :model-value="localSelectedCountryIds.includes(country.Code)"
                @update:model-value="(value) => handleCountryToggle(country.Code, value)"
              />
              <div v-if="filteredCountries.length === 0" class="py-4 text-center text-gray-500">
                {{ $t('ibe.search.no-results', 'No results found') }}
              </div>
            </div>
            <div class="flex justify-end pt-5 pb-2 border-t !mt-0">
              <BaseButton
                variant="primary"
                class="!w-full"
                @click="() => handleApplyCountries(close)"
              >
                {{ $t('ibe.common.apply', 'Apply') }}
              </BaseButton>
            </div>
          </div>
        </template>
      </ContentDropdown>

      <button
        type="button"
        :class="[
          dateButtonClasses,
          { 'opacity-50 pointer-events-none': isDateDisabled, 'search-pulse': isPulsing },
        ]"
        :aria-expanded="modalStore.isModalOpen(ModalType.DATE_PICKER)"
        :aria-disabled="isDateDisabled || undefined"
        :aria-label="dateAriaLabel"
        class="relative z-10 cursor-pointer"
        @mouseenter="hoverState.date = true"
        @mouseleave="hoverState.date = false"
        @click="handleDateClick"
      >
        <span class="text-sm font-medium lg:text-base">{{ dateDisplayLabel }}</span>
      </button>

      <ContentDropdown
        ref="companionsDropdownRef"
        placement="bottom-end"
        :close-on-click="false"
        :disabled="isPaxDisabled"
        :trigger-aria-label="companionsAriaLabel"
        min-width="min-w-[355px]"
        min-height="min-h-[300px]"
        @open="handleCompanionsDropdownOpen"
        @close="handleCompanionsDropdownClose"
      >
        <template #trigger>
          <div
            :class="[companionsButtonClasses, { 'search-pulse': isPulsing }]"
            class="relative z-10"
            @mouseenter="hoverState.companions = true"
            @mouseleave="hoverState.companions = false"
          >
            <span class="text-sm font-medium lg:text-base">{{ companionsDisplayLabel }}</span>
          </div>
        </template>

        <template #default="{ close }">
          <RoomSelector
            ref="roomSelectorRef"
            v-model="localRoomOccupancy"
            @confirm="() => handleRoomOccupancyConfirm(close)"
            @auto-save="handleRoomOccupancyAutoSave"
            @more-options-click="() => close()"
          />
        </template>
      </ContentDropdown>
    </div>

    <button
      :class="searchButtonClasses"
      :disabled="disabled || loading"
      :aria-label="$t('ibe.common.search', 'Search')"
      @click="handleSearchClick"
    >
      <span
        v-if="loading"
        class="w-8 h-8 border-2 border-white rounded-full border-t-transparent animate-spin"
      ></span>
      <i v-else class="w-8 h-8 bg-white icon icon-search"></i>
    </button>
  </div>
</template>

<style scoped>
@keyframes search-attention-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 75, 37, 0.45);
  }
  70%,
  100% {
    box-shadow: 0 0 0 10px rgba(245, 75, 37, 0);
  }
}

.search-pulse {
  animation: search-attention-pulse 1.6s ease-out infinite;
  border-radius: 9999px;
}

@media (prefers-reduced-motion: reduce) {
  .search-pulse {
    animation: none;
  }
}
</style>
